import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Check, Loader2, Phone } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { AdminModeBadge } from '@apps/web/components/admin/admin_mode_badge';
import { Button } from '@apps/web/components/ui/button';
import { DataTable } from '@apps/web/components/ui/data_table';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@apps/web/components/ui/resizable';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    StatusBadge,
    type StatusConfig,
} from '@apps/web/components/ui/status_badge';
import { Textarea } from '@apps/web/components/ui/textarea';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { type ColumnDef } from '@apps/web/lib/table';
import {
    useOrganization,
    usePlaygroundCalls,
    useUser,
    useVoiceCallRecordingUrls,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type CallStatus,
    type PlaygroundCall,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/voice-playground/')({
    component: VoicePlaygroundPage,
    head: () => ({
        meta: [
            {
                title: 'Voice Playground | Phoebe',
            },
        ],
    }),
});

export { VoicePlaygroundContent };

const AGENT_NOTES_STORAGE_KEY = 'voice-playground-agent-notes';

// Define status configurations matching the pattern from contact-attempt-status-badge

const voiceCallStatusConfig: Record<CallStatus, StatusConfig> = {
    ongoing: {
        label: 'In Progress',
        dotColor: 'bg-blue-500',
        backgroundColor: 'bg-blue-100 dark:bg-blue-950/50',
        textColor: 'text-blue-700 dark:text-blue-300',
    },
    ended: {
        label: 'Ended',
        dotColor: 'bg-green-500',
        backgroundColor: 'bg-green-100 dark:bg-green-950/50',
        textColor: 'text-green-700 dark:text-green-300',
    },
};

function StatusCell({ call }: { call: PlaygroundCall }) {
    const config = voiceCallStatusConfig[call.status];
    return <StatusBadge status={config} />;
}

const createColumns = (timezone: string): Array<ColumnDef<PlaygroundCall>> => [
    {
        accessorKey: 'created_at',
        header: 'Date',
        cell: ({ row }) => {
            const iso = row.getValue('created_at') as string;
            return (
                <div className="text-sm">
                    {formatDateAndTime(iso, timezone)}
                </div>
            );
        },
    },
    {
        accessorKey: 'agent_notes',
        header: 'Agent Notes',
        cell: ({ row }) => {
            const notes = row.getValue('agent_notes') as string | null;
            if (!notes) {
                return '-';
            }
            return (
                <div className="max-w-[200px] truncate text-sm" title={notes}>
                    {notes}
                </div>
            );
        },
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusCell call={row.original} />,
    },
];

type VoicePlaygroundContentProps = {
    readonly initialCallId?: string | null;
};

function VoicePlaygroundContent({
    initialCallId = null,
}: VoicePlaygroundContentProps) {
    const router = useRouter();
    const adminMode = useAdminMode();
    const { timezone } = useOrganizationTimezone();
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(
        organizationId,
        {
            reason: 'voice-playground',
        },
        adminMode,
    );
    const { data: user } = useUser({ reason: 'voice-playground' });
    const [phoneNumber, setPhoneNumber] = useState('');
    const [personality, setPersonality] = useState('');
    const [agentNotes, setAgentNotes] = useState(() => {
        // Load saved notes from localStorage on mount
        return localStorage.getItem(AGENT_NOTES_STORAGE_KEY) || '';
    });
    const [isCalling, setIsCalling] = useState(false);
    const [selectedCallId, setSelectedCallId] = useState<string | null>(
        initialCallId,
    );
    const [showSavedIndicator, setShowSavedIndicator] = useState(false);

    // Update selectedCallId when initialCallId changes (for navigation)
    useEffect(() => {
        setSelectedCallId(initialCallId);
    }, [initialCallId]);

    const [caregiverFirstName, setCaregiverFirstName] = useState('Sarah');
    const [shiftDate, setShiftDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    });
    const [shiftStartTime, setShiftStartTime] = useState('09:00');
    const [shiftEndTime, setShiftEndTime] = useState('17:00');
    const [patientFirstName, setPatientFirstName] = useState('');
    const [patientIdentifier, setPatientIdentifier] = useState('');
    const [shiftLocation, setShiftLocation] = useState(
        '123 Main St, Springfield',
    );
    const [shiftCity, setShiftCity] = useState('Springfield');
    const [shiftState, setShiftState] = useState('IL');
    const [roleRequired, setRoleRequired] = useState(
        'Certified Nursing Assistant',
    );

    const { data: callHistory = [], mutate: mutateHistory } =
        usePlaygroundCalls(
            organizationId,
            { reason: 'voice-playground' },
            adminMode,
        );

    const selectedCall = callHistory?.find(
        (call) => call.id === selectedCallId,
    );

    const voiceCallIdsForRecordings = useMemo(() => {
        if (!selectedCall?.recording_url) {
            return null;
        }
        const isS3Url =
            selectedCall.recording_url.includes('s3.amazonaws.com') ||
            selectedCall.recording_url.startsWith('s3://');
        return isS3Url ? [selectedCall.id] : null;
    }, [selectedCall]);

    const { data: recordingUrls } = useVoiceCallRecordingUrls(
        organizationId,
        voiceCallIdsForRecordings,
    );

    const [liveTranscript, setLiveTranscript] = useState<string | null>(null);

    useListenForOrganizationEvent('voice_call_updated', async (event) => {
        if (event.resource.type !== 'voice_call') {
            return;
        }
        const voice_call_id = event.resource.id;
        const shouldMutate =
            (selectedCallId && voice_call_id === selectedCallId) ||
            !!callHistory?.some((c) => c.id === voice_call_id);
        if (shouldMutate) {
            await mutateHistory();
        }
    });

    useEffect(() => {
        if (selectedCall) {
            setLiveTranscript(selectedCall.transcript);
        } else if (!selectedCallId) {
            setLiveTranscript(null);
        }
    }, [selectedCall, selectedCallId]);

    // Auto-save agent notes to localStorage
    useEffect(() => {
        localStorage.setItem(AGENT_NOTES_STORAGE_KEY, agentNotes);
    }, [agentNotes]);

    const handleAgentNotesBlur = () => {
        // Show saved indicator on blur
        setShowSavedIndicator(true);

        // Hide the saved indicator after 2 seconds
        setTimeout(() => {
            setShowSavedIndicator(false);
        }, 2000);
    };

    const handleCall = async () => {
        if (adminMode === 'sandbox') {
            return;
        }
        if (!phoneNumber) {
            toast.error('Please enter a phone number.', {
                persistNotification: false,
            });
            return;
        }
        setIsCalling(true);
        try {
            await getApiClient().post({
                url: '/organizations/{organization_id}/voice-playground/create-call',
                pathParams: { organization_id: organizationId! },
                mode: adminMode,
                body: {
                    agent_type: 'callout_multi_shift',
                    phone_number: phoneNumber,
                    agent_notes: agentNotes,
                    shift_details: {
                        caregiver_first_name: caregiverFirstName,
                        shift_date: shiftDate || '',
                        shift_start_time: shiftStartTime,
                        shift_end_time: shiftEndTime,
                        patient_first_name: patientFirstName,
                        patient_identifier: patientIdentifier,
                        shift_location: shiftLocation,
                        shift_city: shiftCity,
                        shift_state: shiftState,
                        role_required: roleRequired,
                        organization_name:
                            organization?.name || 'Your Organization',
                    },
                    personality:
                        user?.admin && personality ? personality : undefined,
                },
            });
            toast.success(`Calling ${phoneNumber}...`, {
                persistNotification: false,
            });
            await mutateHistory();
        } catch (error) {
            toast.error('Failed to initiate call.', {
                persistNotification: false,
            });
            console.error(error);
        } finally {
            setIsCalling(false);
        }
    };

    return (
        <div className="flex h-[calc(100vh-3.5rem)] flex-col">
            <ResizablePanelGroup
                orientation="horizontal"
                className="min-h-0 flex-1"
            >
                <ResizablePanel defaultSize={70} minSize={25}>
                    <ScrollArea className="h-full">
                        <div className="p-4">
                            <div className="mb-8">
                                <h1 className="text-xl font-semibold">
                                    Voice Playground
                                </h1>
                                <p className="text-muted-foreground mt-1 text-sm">
                                    Experiment with Phoebe voice calls
                                </p>
                            </div>
                            <div className="space-y-8">
                                <div>
                                    <Label
                                        htmlFor="phone-number"
                                        className="mb-2 block"
                                    >
                                        Phone Number
                                    </Label>
                                    <Input
                                        id="phone-number"
                                        placeholder="(555) 123-4567"
                                        value={phoneNumber}
                                        onChange={(e) =>
                                            setPhoneNumber(e.target.value)
                                        }
                                    />
                                </div>
                                <div>
                                    <div className="mb-2 flex items-center justify-between">
                                        <Label htmlFor="agent-notes">
                                            Agent Notes
                                        </Label>
                                        {showSavedIndicator && (
                                            <span className="flex items-center gap-1 text-xs text-green-600">
                                                <Check className="h-3 w-3" />
                                                Saved
                                            </span>
                                        )}
                                    </div>
                                    <Textarea
                                        id="agent-notes"
                                        placeholder="What should Phoebe communicate about the client? Treat Phoebe like a teammate: share the notes needed to run the call."
                                        value={agentNotes}
                                        onChange={(e) =>
                                            setAgentNotes(e.target.value)
                                        }
                                        onBlur={handleAgentNotesBlur}
                                        className="mt-1 min-h-[200px]"
                                    />
                                </div>
                                {user?.admin && (
                                    <div>
                                        <Label
                                            htmlFor="personality"
                                            className="mb-2 block"
                                        >
                                            Agent Personality (Admin Only)
                                        </Label>
                                        <Textarea
                                            id="personality"
                                            placeholder="Warm and genuinely caring, like a friendly neighbor checking in. Takes a breath before diving into business..."
                                            value={personality}
                                            onChange={(e) =>
                                                setPersonality(e.target.value)
                                            }
                                            className="mt-1 min-h-[100px]"
                                        />
                                    </div>
                                )}
                                <div className="mt-6 space-y-4 rounded-lg border p-4">
                                    <h3 className="text-sm font-medium">
                                        Shift Details
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label
                                                htmlFor="caregiver-name"
                                                className="mb-1 block text-xs"
                                            >
                                                Caregiver First Name
                                            </Label>
                                            <Input
                                                id="caregiver-name"
                                                value={caregiverFirstName}
                                                onChange={(e) =>
                                                    setCaregiverFirstName(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <Label
                                                htmlFor="role-required"
                                                className="mb-1 block text-xs"
                                            >
                                                Role Required
                                            </Label>
                                            <Select
                                                value={roleRequired}
                                                onValueChange={setRoleRequired}
                                            >
                                                <SelectTrigger
                                                    id="role-required"
                                                    className="h-8 w-full text-sm"
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="N/A">
                                                        N/A
                                                    </SelectItem>
                                                    <SelectItem value="Certified Nursing Assistant">
                                                        Certified Nursing
                                                        Assistant
                                                    </SelectItem>
                                                    <SelectItem value="Registered Nurse">
                                                        Registered Nurse
                                                    </SelectItem>
                                                    <SelectItem value="Licensed Practical Nurse">
                                                        Licensed Practical Nurse
                                                    </SelectItem>
                                                    <SelectItem value="Home Health Aide">
                                                        Home Health Aide
                                                    </SelectItem>
                                                    <SelectItem value="Personal Care Assistant">
                                                        Personal Care Assistant
                                                    </SelectItem>
                                                    <SelectItem value="Companion">
                                                        Companion
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <Label
                                                htmlFor="shift-date"
                                                className="mb-1 block text-xs"
                                            >
                                                Shift Date
                                            </Label>
                                            <Input
                                                id="shift-date"
                                                type="date"
                                                value={shiftDate}
                                                onChange={(e) =>
                                                    setShiftDate(e.target.value)
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <Label
                                                htmlFor="shift-start"
                                                className="mb-1 block text-xs"
                                            >
                                                Start Time
                                            </Label>
                                            <Input
                                                id="shift-start"
                                                type="time"
                                                value={shiftStartTime}
                                                onChange={(e) =>
                                                    setShiftStartTime(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <Label
                                                htmlFor="shift-end"
                                                className="mb-1 block text-xs"
                                            >
                                                End Time
                                            </Label>
                                            <Input
                                                id="shift-end"
                                                type="time"
                                                value={shiftEndTime}
                                                onChange={(e) =>
                                                    setShiftEndTime(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label
                                                htmlFor="patient-name"
                                                className="mb-1 block text-xs"
                                            >
                                                Patient First Name
                                            </Label>
                                            <Input
                                                id="patient-name"
                                                placeholder="John"
                                                value={patientFirstName}
                                                onChange={(e) =>
                                                    setPatientFirstName(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <Label
                                                htmlFor="patient-id"
                                                className="mb-1 block text-xs"
                                            >
                                                Patient Last Name
                                            </Label>
                                            <Input
                                                id="patient-id"
                                                placeholder="Smith"
                                                value={patientIdentifier}
                                                onChange={(e) =>
                                                    setPatientIdentifier(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label
                                            htmlFor="shift-location"
                                            className="mb-1 block text-xs"
                                        >
                                            Shift Address
                                        </Label>
                                        <Input
                                            id="shift-location"
                                            placeholder="123 Main St"
                                            value={shiftLocation}
                                            onChange={(e) =>
                                                setShiftLocation(e.target.value)
                                            }
                                            className="h-8 text-sm"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label
                                                htmlFor="shift-city"
                                                className="mb-1 block text-xs"
                                            >
                                                City
                                            </Label>
                                            <Input
                                                id="shift-city"
                                                placeholder="Springfield"
                                                value={shiftCity}
                                                onChange={(e) =>
                                                    setShiftCity(e.target.value)
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <Label
                                                htmlFor="shift-state"
                                                className="mb-1 block text-xs"
                                            >
                                                State
                                            </Label>
                                            <Input
                                                id="shift-state"
                                                placeholder="IL"
                                                value={shiftState}
                                                onChange={(e) =>
                                                    setShiftState(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <Button
                                        onClick={handleCall}
                                        disabled={
                                            isCalling || adminMode === 'sandbox'
                                        }
                                        variant="default"
                                    >
                                        {isCalling ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <Phone className="mr-2 h-4 w-4" />
                                        )}
                                        Place Call
                                    </Button>
                                    <div className="flex flex-wrap items-center justify-end gap-2">
                                        <AdminModeBadge mode={adminMode} />
                                        {adminMode === 'sandbox' && (
                                            <p className="text-muted-foreground text-xs">
                                                Test calls dial real telephony
                                                and require live mode.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <h2 className="mt-6 mb-2 border-t pt-6 text-lg font-semibold">
                                Call History
                            </h2>
                            <div className="flex-1 overflow-hidden">
                                {callHistory && callHistory.length === 0 ? (
                                    <div className="mt-8 text-center">
                                        <h3 className="text-base font-medium">
                                            No calls yet
                                        </h3>
                                        <p className="text-muted-foreground mt-1 text-sm">
                                            Place your first call to see it
                                            appear in the history. Your call
                                            transcripts and recordings will be
                                            saved here.
                                        </p>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={createColumns(timezone)}
                                        data={callHistory || []}
                                        getRowNavigation={(call) => {
                                            const routeOptions = {
                                                to: '/admin/voice-playground/$call-id',
                                                params: { 'call-id': call.id },
                                            } as const;
                                            return {
                                                href: router.buildLocation(
                                                    routeOptions,
                                                ).href,
                                                navigate: () => {
                                                    void router.navigate({
                                                        ...routeOptions,
                                                        replace:
                                                            !!initialCallId,
                                                    });
                                                },
                                            };
                                        }}
                                        getRowClassName={(call) =>
                                            call.id === selectedCallId
                                                ? 'bg-accent'
                                                : ''
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    </ScrollArea>
                </ResizablePanel>
                <ResizableHandle withHandle={true} />
                <ResizablePanel defaultSize={30} minSize={30}>
                    <div className="flex h-full flex-col overflow-hidden p-4">
                        {!selectedCallId && !liveTranscript ? (
                            <div className="flex h-full items-center justify-center">
                                <div className="text-center">
                                    <h3 className="text-base font-medium">
                                        No call selected
                                    </h3>
                                    <p className="text-muted-foreground mt-1 text-sm">
                                        We’ll show you the call transcript,
                                        recording, and call summary here
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-hidden">
                                {selectedCall?.status && (
                                    <div className="flex justify-end border-b p-3">
                                        <StatusBadge
                                            status={
                                                voiceCallStatusConfig[
                                                    selectedCall.status
                                                ]
                                            }
                                        />
                                    </div>
                                )}
                                {selectedCall?.recording_url && (
                                    <div className="border-b p-4">
                                        <p className="mb-2 text-sm font-medium">
                                            Recording
                                        </p>
                                        <VoiceCallAudioPlayer
                                            voiceCall={{
                                                id: selectedCall.id,
                                                recording_url:
                                                    selectedCall.recording_url,
                                            }}
                                            prefetchedRecordingUrl={
                                                recordingUrls?.[selectedCall.id]
                                            }
                                            className="h-10 w-full focus:outline-none"
                                        />
                                    </div>
                                )}
                                {selectedCall?.call_summary && (
                                    <div className="border-b bg-blue-50 p-4 dark:bg-blue-950/50">
                                        <p className="mb-2 text-sm font-medium">
                                            Call Summary
                                        </p>
                                        <p className="text-foreground/80 text-sm">
                                            {selectedCall.call_summary}
                                        </p>
                                    </div>
                                )}
                                <ScrollArea className="flex-1">
                                    <div className="p-4">
                                        {liveTranscript ? (
                                            <React.Fragment>
                                                <p className="mb-3 text-sm font-medium">
                                                    Transcript
                                                </p>
                                                <div className="space-y-2">
                                                    {liveTranscript
                                                        .split('\n')
                                                        .filter(
                                                            (line) =>
                                                                line.trim() !==
                                                                '',
                                                        )
                                                        .map((line, index) => {
                                                            const match =
                                                                line.match(
                                                                    /^(Agent|User):\s*(.+)$/,
                                                                );
                                                            if (match) {
                                                                const [
                                                                    ,
                                                                    speaker,
                                                                    message,
                                                                ] = match;
                                                                return (
                                                                    <div
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="flex gap-3 text-sm"
                                                                    >
                                                                        <span className="min-w-[50px] font-medium">
                                                                            {
                                                                                speaker
                                                                            }
                                                                            :
                                                                        </span>
                                                                        <span className="flex-1">
                                                                            {
                                                                                message
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                );
                                                            }
                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className="text-sm"
                                                                >
                                                                    {line}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </React.Fragment>
                                        ) : selectedCall?.transcript ? (
                                            <React.Fragment>
                                                <p className="mb-3 text-sm font-medium">
                                                    Transcript
                                                </p>
                                                <div className="space-y-2">
                                                    {selectedCall.transcript
                                                        .split('\n')
                                                        .filter(
                                                            (line) =>
                                                                line.trim() !==
                                                                '',
                                                        )
                                                        .map((line, index) => {
                                                            const match =
                                                                line.match(
                                                                    /^(Agent|User):\s*(.+)$/,
                                                                );
                                                            if (match) {
                                                                const [
                                                                    ,
                                                                    speaker,
                                                                    message,
                                                                ] = match;
                                                                return (
                                                                    <div
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="flex gap-3 text-sm"
                                                                    >
                                                                        <span className="min-w-[50px] font-medium">
                                                                            {
                                                                                speaker
                                                                            }
                                                                            :
                                                                        </span>
                                                                        <span className="flex-1">
                                                                            {
                                                                                message
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                );
                                                            }
                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className="text-sm"
                                                                >
                                                                    {line}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </React.Fragment>
                                        ) : (
                                            <div className="flex h-full items-center justify-center">
                                                <div className="text-center">
                                                    {selectedCall &&
                                                        selectedCall.status ===
                                                            'ongoing' && (
                                                            <React.Fragment>
                                                                <div className="mb-4 text-2xl">
                                                                    🎙️
                                                                </div>
                                                                <h3 className="mb-2 text-base font-medium">
                                                                    Call in
                                                                    progress…
                                                                </h3>
                                                            </React.Fragment>
                                                        )}
                                                    {selectedCall &&
                                                        selectedCall.status ===
                                                            'ongoing' && (
                                                            <div className="text-muted-foreground mt-6 space-y-2 text-left text-sm">
                                                                <p className="font-medium">
                                                                    What you’ll
                                                                    see here:
                                                                </p>
                                                                <p>
                                                                    Call
                                                                    transcript
                                                                </p>
                                                                <p>Recording</p>
                                                                <p>
                                                                    Call summary
                                                                </p>
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </ScrollArea>
                            </div>
                        )}
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );
}

function VoicePlaygroundPage() {
    return <VoicePlaygroundContent initialCallId={null} />;
}
